/**
 * sirus/ShopPanel — R232q21 Phase 4.
 *
 * Sirus 3.3.5a-style in-game shop. Toggled via the «Магазин» micro-button
 * (panel flag `shop`). Four tabs:
 *   – Кристаллы Шторма (currency packs in roubles)
 *   – Косметика (skins / mounts / transmog placeholders)
 *   – Сервис (rename / race / faction / server change)
 *   – Battle Pass (tier list with rewards + current XP)
 *
 * LavaTop integration is deferred. «Купить» buttons currently fire a
 * console.log so the click flow is verifiable end-to-end. When LavaTop
 * lands we replace the click handler with a payment-init call.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Mock data
 * ──────────────────────────────────────────────────────────────── */

type TabId = 'crystals' | 'cosmetics' | 'services' | 'battlepass';

const TABS: { id: TabId; label: string }[] = [
  { id: 'crystals',   label: 'Кристаллы Шторма' },
  { id: 'cosmetics',  label: 'Косметика' },
  { id: 'services',   label: 'Сервис' },
  { id: 'battlepass', label: 'Battle Pass' },
];

interface CrystalPack {
  id: string;
  amount: number;
  rub: number;
  bonus?: string;
}

const CRYSTAL_PACKS: CrystalPack[] = [
  { id: 'cp1', amount: 100,    rub: 99    },
  { id: 'cp2', amount: 500,    rub: 499,  bonus: '+25 бонусом' },
  { id: 'cp3', amount: 1000,   rub: 999,  bonus: '+100 бонусом' },
  { id: 'cp4', amount: 2500,   rub: 2499, bonus: '+350 бонусом' },
  { id: 'cp5', amount: 5000,   rub: 4999, bonus: '+800 бонусом' },
  { id: 'cp6', amount: 10_000, rub: 9999, bonus: '+2000 бонусом · набор лучшего покупателя' },
];

interface CosmeticItem {
  id: string;
  name: string;
  kind: string;
  price: number;
}

const COSMETICS: CosmeticItem[] = [
  { id: 'cs1', name: 'Лазурный плащ зимы',     kind: 'Облик плаща',  price: 450 },
  { id: 'cs2', name: 'Багряный гребень',       kind: 'Облик шлема',  price: 380 },
  { id: 'cs3', name: 'Серебряный единорог',    kind: 'Маунт',        price: 1800 },
  { id: 'cs4', name: 'Облик «Полночь»',        kind: 'Трансмог сет', price: 2200 },
  { id: 'cs5', name: 'Питомец-светлячок',      kind: 'Питомец',      price: 350 },
  { id: 'cs6', name: 'Облик дракона тумана',   kind: 'Маунт',        price: 2400 },
];

interface ServiceItem {
  id: string;
  name: string;
  desc: string;
  price: number;
}

const SERVICES: ServiceItem[] = [
  { id: 'sv1', name: 'Переименование',     desc: 'Сменить имя персонажа.',                       price: 800 },
  { id: 'sv2', name: 'Смена расы',         desc: 'Изменить расу с сохранением класса и прогресса.', price: 2000 },
  { id: 'sv3', name: 'Смена фракции',      desc: 'Перейти в противоположную фракцию.',           price: 2500 },
  { id: 'sv4', name: 'Перевод на реалм',   desc: 'Переместить персонажа на другой реалм.',       price: 1500 },
];

interface BPTier {
  tier: number;
  name: string;
  free?: string;
  premium: string;
  unlocked: boolean;
}

const BP = {
  currentTier: 14,
  currentXp: 6_840,
  xpPerTier: 10_000,
  totalTiers: 60,
};

const BP_TIERS: BPTier[] = Array.from({ length: 12 }, (_, i) => {
  const tier = i + 10;
  return {
    tier,
    name: `Уровень ${tier}`,
    free: i % 3 === 0 ? '50 кристаллов' : undefined,
    premium: ['Облик плаща', 'Маунт-фрагмент', 'Сундук редкого', 'Эмоция'][i % 4],
    unlocked: tier <= BP.currentTier,
  };
});

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function ShopPanel() {
  const close = () => useUiPanels.getState().set('shop', false);
  const [tab, setTab] = useState<TabId>('crystals');

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const buy = (label: string) => {
    // eslint-disable-next-line no-console
    console.log('TODO LavaTop integration', label);
  };

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()}>
        <SirusFrame size="md" style={{ width: 700, height: 580, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Магазин</span>
            <span style={balancePillStyle}>Баланс: 0 ◈</span>
            <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
          </div>

          {/* Tabs */}
          <div style={tabRowStyle}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    ...tabBtnStyle,
                    background: active ? 'rgba(201,169,106,0.15)' : 'transparent',
                    color: active ? '#FFD479' : 'var(--aiwari-fg-muted, #B19B7E)',
                    borderBottomColor: active ? 'var(--aiwari-gold, #FAB300)' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Body */}
          <div style={bodyStyle}>
            {tab === 'crystals' && <CrystalsTab onBuy={buy} />}
            {tab === 'cosmetics' && <CosmeticsTab onBuy={buy} />}
            {tab === 'services' && <ServicesTab onBuy={buy} />}
            {tab === 'battlepass' && <BattlePassTab />}
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Tabs
 * ──────────────────────────────────────────────────────────────── */

function CrystalsTab({ onBuy }: { onBuy: (label: string) => void }) {
  return (
    <div style={cardGridStyle}>
      {CRYSTAL_PACKS.map((p) => (
        <div key={p.id} style={crystalCardStyle}>
          <div style={crystalAmountStyle}>{p.amount.toLocaleString('ru-RU')} ◈</div>
          {p.bonus && <div style={bonusStyle}>{p.bonus}</div>}
          <div style={priceStyle}>{p.rub.toLocaleString('ru-RU')} ₽</div>
          <button type="button" style={primaryBtnStyle} onClick={() => onBuy(`crystals:${p.id}`)}>Купить</button>
        </div>
      ))}
    </div>
  );
}

function CosmeticsTab({ onBuy }: { onBuy: (label: string) => void }) {
  return (
    <div style={cardGridStyle}>
      {COSMETICS.map((c) => (
        <div key={c.id} style={cosmeticCardStyle}>
          <div style={{
            height: 80,
            background: 'radial-gradient(circle at 35% 30%, #5C4520, #0B0907)',
            border: '1px solid var(--aiwari-frame-edge, #5C4520)',
            marginBottom: 6,
          }} />
          <div style={nameStyle}>{c.name}</div>
          <div style={subStyle}>{c.kind}</div>
          <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ ...priceStyle, fontSize: 13 }}>{c.price} ◈</span>
            <button type="button" style={primaryBtnStyle} onClick={() => onBuy(`cosmetic:${c.id}`)}>Купить</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ServicesTab({ onBuy }: { onBuy: (label: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {SERVICES.map((s) => (
        <div key={s.id} style={serviceRowStyle}>
          <div style={{ flex: 1 }}>
            <div style={nameStyle}>{s.name}</div>
            <div style={subStyle}>{s.desc}</div>
          </div>
          <div style={priceStyle}>{s.price} ◈</div>
          <button type="button" style={primaryBtnStyle} onClick={() => onBuy(`service:${s.id}`)}>Купить</button>
        </div>
      ))}
    </div>
  );
}

function BattlePassTab() {
  const xpPct = Math.min(100, Math.round((BP.currentXp / BP.xpPerTier) * 100));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        padding: 10,
        background: 'rgba(11,9,7,0.5)',
        border: '1px solid var(--aiwari-frame-edge, #5C4520)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={nameStyle}>Уровень {BP.currentTier} / {BP.totalTiers}</span>
          <span style={{ ...subStyle, fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)', color: '#FFD479' }}>
            {BP.currentXp.toLocaleString('ru-RU')} / {BP.xpPerTier.toLocaleString('ru-RU')} XP
          </span>
        </div>
        <div style={xpTrackStyle}>
          <div style={{ ...xpFillStyle, width: `${xpPct}%` }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {BP_TIERS.map((t) => (
          <div key={t.tier} style={{
            ...bpRowStyle,
            opacity: t.unlocked ? 1 : 0.6,
            borderColor: t.unlocked ? '#7A5A18' : '#2A2014',
          }}>
            <div style={{
              width: 36, height: 36,
              borderRadius: '50%',
              background: t.unlocked ? 'radial-gradient(circle, #FFD479, #5C4520)' : 'radial-gradient(circle, #2A2014, #0B0907)',
              border: `1px solid ${t.unlocked ? '#FFD479' : '#5C4520'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
              fontSize: 12,
              color: '#0B0907',
            }}>
              {t.tier}
            </div>
            <div style={{ flex: 1 }}>
              <div style={subStyle}>{t.free ? `Бесплатно: ${t.free}` : 'Бесплатно: —'}</div>
              <div style={{ ...nameStyle, color: '#FFD479', fontSize: 12 }}>Премиум: {t.premium}</div>
            </div>
            {t.unlocked && <span style={{ ...subStyle, color: '#7BC97B' }}>Открыто</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  paddingBottom: 8,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  flex: 1,
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 18,
  letterSpacing: 0.8,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const balancePillStyle: CSSProperties = {
  padding: '3px 10px',
  background: 'rgba(11,9,7,0.7)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  borderRadius: 999,
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 12,
  color: '#FFD479',
};

const closeBtnStyle: CSSProperties = {
  width: 28, height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer',
  fontFamily: 'inherit',
};

const tabRowStyle: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
  marginTop: 6,
};

const tabBtnStyle: CSSProperties = {
  padding: '8px 14px',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const bodyStyle: CSSProperties = {
  height: 460,
  marginTop: 10,
  overflowY: 'auto',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const cardGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 10,
};

const crystalCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 12,
  background: 'linear-gradient(180deg, #1F1813, #0B0907)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  gap: 6,
};

const crystalAmountStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 20,
  color: '#FFD479',
};

const cosmeticCardStyle: CSSProperties = {
  padding: 8,
  background: 'linear-gradient(180deg, #1F1813, #0B0907)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const serviceRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: 10,
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const nameStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const subStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontStyle: 'italic',
};

const bonusStyle: CSSProperties = {
  fontSize: 11,
  color: '#7BC97B',
  fontStyle: 'italic',
  textAlign: 'center',
};

const priceStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 14,
  color: '#FFD479',
};

const primaryBtnStyle: CSSProperties = {
  padding: '5px 14px',
  background: 'linear-gradient(180deg, #2A2014, #1F1813)',
  border: '1px solid var(--aiwari-gold-dark, #7A5A18)',
  color: 'var(--aiwari-gold-soft, #FFD479)',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  cursor: 'pointer',
};

const xpTrackStyle: CSSProperties = {
  height: 10,
  background: 'rgba(0,0,0,0.6)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  overflow: 'hidden',
};

const xpFillStyle: CSSProperties = {
  height: '100%',
  background: 'linear-gradient(90deg, #C9A96A, #FFD479)',
  boxShadow: '0 0 6px rgba(255,212,121,0.5)',
  transition: 'width 200ms',
};

const bpRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 8px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

export default ShopPanel;
